import { useState, useCallback, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import Lobby from "@/components/Lobby";
import WorldView from "@/components/WorldView";
import AuthorStudio from "@/components/AuthorStudio";
import AboutModal from "@/components/AboutModal";
import MemoryAlbum from "@/components/MemoryAlbum";
import SettingsMenu from "@/components/SettingsMenu";
import TopControls from "@/components/TopControls";
import CustomCursor from "@/components/CustomCursor";
import LoadingScreen from "@/components/LoadingScreen";
import PortalTransition from "@/components/PortalTransition";
import { GameProvider, useGame } from "@/context/GameContext";
import { sound } from "@/lib/sound";
import { WORLDS, AUTHOR_STUDIO } from "@/data";
import "@/App.css";

function AppInner() {
  const { state, visit } = useGame();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("lobby"); // lobby | world | studio
  const [activeWorldId, setActiveWorldId] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [albumOpen, setAlbumOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [transition, setTransition] = useState(null);

  const activeWorld = WORLDS.find((w) => w.id === activeWorldId);

  const enterWorld = useCallback(
    (id) => {
      const world = WORLDS.find((w) => w.id === id);
      sound.playPortal(id);
      setTransition({ color: world.accent });
      setActiveWorldId(id);
      setView("world");
      visit(id);
      setTimeout(() => setTransition(null), 800);
    },
    [visit]
  );

  const enterStudio = useCallback(() => {
    sound.playPortal("studio");
    setTransition({ color: AUTHOR_STUDIO.accent });
    setView("studio");
    setTimeout(() => setTransition(null), 800);
  }, []);

  const backToLobby = useCallback(() => {
    sound.stopMusic();
    setView("lobby");
    setActiveWorldId(null);
  }, []);

  // background music management
  useEffect(() => {
    if (state.settings.music && view === "world" && activeWorldId) sound.startMusic(activeWorldId);
    else if (state.settings.music && view === "studio") sound.startMusic("studio");
    else sound.stopMusic();
  }, [state.settings.music, view, activeWorldId]);

  const cursorColor =
    view === "studio"
      ? AUTHOR_STUDIO.accent
      : view === "world" && activeWorld
      ? activeWorld.accent
      : "#FF2A6D";
  const currentWorldForSettings =
    view === "world" ? activeWorldId : view === "studio" ? "studio" : null;

  return (
    <div className="wv-app min-h-screen bg-[#050505] text-white">
      <CustomCursor color={cursorColor} />

      <AnimatePresence>
        {loading && <LoadingScreen key="loading" onDone={() => setLoading(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {transition && <PortalTransition key="pt" color={transition.color} />}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {view === "lobby" && (
          <Lobby key="lobby" onEnterWorld={enterWorld} onEnterStudio={enterStudio} />
        )}
        {view === "world" && activeWorld && (
          <WorldView key={`world-${activeWorld.id}`} world={activeWorld} onBack={backToLobby} />
        )}
        {view === "studio" && <AuthorStudio key="studio" onBack={backToLobby} />}
      </AnimatePresence>

      {!loading && (
        <TopControls
          showAbout={view === "lobby"}
          onAbout={() => setAboutOpen(true)}
          onAlbum={() => setAlbumOpen(true)}
          onSettings={() => setSettingsOpen(true)}
        />
      )}

      <AboutModal open={aboutOpen} onOpenChange={setAboutOpen} />
      <SettingsMenu
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        currentWorld={currentWorldForSettings}
      />
      <AnimatePresence>
        {albumOpen && <MemoryAlbum key="album" onClose={() => setAlbumOpen(false)} />}
      </AnimatePresence>

      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
}

export default function App() {
  return (
    <GameProvider>
      <AppInner />
    </GameProvider>
  );
}
